// RUTA: src/lib/streakMessages.ts
//
// Mensajes motivacionales escalonados por tier de racha.
//
// Filosofía:
//
//   1. AFIRMAR lo que el cliente HIZO BIEN, no amenazar con perder.
//      Mal: "No la rompas mañana"
//      Bien: "Vas muy bien — continúa así mañana"
//
//   2. CONTEXTO DEL DÍA importa. Si hoy aún no registras pero tienes
//      racha, el mensaje cambia: "Registra algo hoy para mantener tu
//      racha" en lugar de "X días, vas bien".
//
//   3. NUNCA emojis ambiguos. Ej. "el cardio quema 💪" se lee como
//      "el cardio quema músculo". Los emojis van al inicio o
//      separados del verbo.
//
//   4. Voz del coach: amigable, supportive, en segunda persona y con
//      el nombre del cliente. Sin agresividad ("modo bestia", "no
//      falles").

export interface StreakDisplay {
  emoji: string;
  message: string;
  tier: 0 | 1 | 2 | 3 | 4 | 5;
}

interface StreakOptions {
  /** Nombre de pila del cliente — se inyecta en el mensaje */
  clientFirstName?: string;
  /** Tipo de actividad — afecta el copy */
  activity?: 'nutrition' | 'cardio';
  /** Seed determinístico para escoger variante (típicamente
   *  clientId + actividad + fecha-hoy). Mantiene la variante estable
   *  durante el día pero la cambia entre días. */
  seed?: string;
  /** ¿El cliente ya registró HOY?
   *   - true: mensajes que afirman lo que hizo hoy
   *   - false: mensajes que invitan a registrar hoy para mantener
   *     la racha (sin amenaza, en tono coach)
   *  Si no se pasa, asumimos true (compat con código viejo). */
  loggedToday?: boolean;
}

// =================================================================
// VARIANTES POR TIER — versión "ya registró hoy" (afirmando)
// =================================================================
//
// {name} → ", Joel"  (vacío si no hay nombre)
// {n}    → número de la racha

const NUTRITION_LOGGED_TODAY: Record<number, string[]> = {
  0: [
    // No alcanza este tier si hay racha — usado solo si streak=0
    'Empieza tu racha hoy{name}. Registrar 1 alimento ya cuenta.',
    'Hoy es buen día para empezar a registrar{name}.',
  ],
  1: [
    '¡Bien hecho{name}! Registraste hoy. Sigue así mañana.',
    '¡Vas bien{name}! Hoy contó — mantén el ritmo.',
    'Buen primer día{name}. La constancia es lo que cuenta.',
  ],
  2: [
    '¡2 días seguidos{name}! Vas tomando ritmo, sigue así.',
    'Dos días registrando{name}. Vas muy bien — continúa.',
  ],
  3: [ // 3-6
    '{n} días seguidos{name}. Vas muy bien, continúa así.',
    'Llevas {n} días registrando{name}. El hábito se está formando.',
    '{n} días de constancia{name}. Sigue así, vas en camino.',
  ],
  4: [ // 7-13
    '🙌 Una semana completa{name}. Estás haciendo un trabajo excelente.',
    '{n} días registrando{name} — vas muy bien, continúa así.',
    'Una semana entera{name}. La consistencia se nota — sigue así.',
  ],
  5: [ // 14-29
    '🚀 {n} días{name}. Esto ya es un hábito sólido — continúa.',
    '{n} días registrando{name}. Vas excelente, sigue así.',
  ],
  6: [ // 30-59
    '🏆 {n} días{name}. Esto es disciplina pura — sigue así.',
    '{n} días seguidos{name}. Estás haciendo un trabajo enorme.',
  ],
  7: [ // 60+
    '👑 {n} días{name}. Este nivel de constancia es admirable.',
    '{n} días sin parar{name}. Eres un ejemplo — continúa así.',
  ],
};

const NUTRITION_NOT_LOGGED_TODAY: Record<number, string[]> = {
  0: [
    // streak=0 y hoy vacío — animación inicial pura
    'Empieza tu racha hoy{name}. Registrar 1 alimento ya cuenta.',
    'Hoy es buen día para empezar{name}. Un solo registro y arrancas.',
  ],
  1: [ // tenía 1 día (ayer) y hoy aún no registra
    'Llevas 1 día{name}. Registra algo hoy para mantener tu racha.',
    'Ayer registraste{name}. Hoy hazlo igual y vas por 2 días.',
  ],
  2: [
    '2 días registrando{name}. Hoy aún no — registra 1 alimento para seguir.',
    'Llevas 2 días{name}. Mantén tu racha, registra algo hoy.',
  ],
  3: [ // 3-6
    '{n} días de racha{name}. Hoy aún no registras — un alimento basta para seguir.',
    'Llevas {n} días{name}. Cuídala — registra algo hoy.',
  ],
  4: [ // 7-13
    'Una semana de racha{name}. Hoy aún no — registra hoy y vas por más.',
    '{n} días seguidos{name}. Mantén tu racha, registra algo hoy.',
  ],
  5: [ // 14-29
    '{n} días{name}. Hoy aún no registras — cuida tu racha con 1 registro.',
    'Llevas {n} días{name}. Un alimento hoy y la racha sigue viva.',
  ],
  6: [ // 30-59
    '🏆 {n} días{name}. Cuida esta racha — registra algo hoy.',
    '{n} días{name}. No la pierdas hoy, basta con 1 registro.',
  ],
  7: [ // 60+
    '👑 {n} días{name}. Tu racha élite vive — registra hoy y sigue.',
  ],
};

// =================================================================
// CARDIO — copy ajustado a la actividad
// =================================================================

const CARDIO_LOGGED_TODAY: Record<number, string[]> = {
  0: [
    'Hoy es buen día para mover el cuerpo{name}.',
    'Una sesión de cardio te separa de empezar{name}.',
  ],
  1: [
    '¡Bien hecho{name}! Hiciste cardio hoy, sigue así mañana.',
    'Buen inicio{name}. Un día de cardio ya cuenta — continúa.',
  ],
  2: [
    '2 días seguidos de cardio{name}. Vas muy bien, sigue así.',
    'Dos días{name}. El corazón te lo agradece — continúa.',
  ],
  3: [ // 3-6
    '{n} días de cardio{name}. Vas muy bien, sigue así.',
    'Llevas {n} días moviéndote{name}. El hábito se está formando.',
  ],
  4: [ // 7-13
    '🙌 Una semana de cardio{name}. Estás haciendo un trabajo excelente.',
    '{n} días seguidos{name} — el cuerpo lo está agradeciendo.',
  ],
  5: [ // 14-29
    '🚀 {n} días{name}. Esto ya es estilo de vida — continúa así.',
    '{n} días de cardio{name}. Vas excelente, sigue así.',
  ],
  6: [ // 30-59
    '🏆 {n} días{name}. Disciplina cardiovascular pura — sigue así.',
    '{n} días seguidos{name}. Tu salud está al máximo, continúa.',
  ],
  7: [ // 60+
    '👑 {n} días{name}. Este nivel es admirable — continúa.',
  ],
};

const CARDIO_NOT_LOGGED_TODAY: Record<number, string[]> = {
  0: [
    'Hoy es buen día para empezar{name}. Una sesión y arrancas.',
    'Empieza tu racha de cardio hoy{name}.',
  ],
  1: [
    '1 día de cardio{name}. Haz una sesión hoy y vas por 2.',
    'Ayer hiciste cardio{name}. Repítelo hoy y mantén el ritmo.',
  ],
  2: [
    '2 días{name}. Hoy aún no — una sesión y la racha sigue.',
  ],
  3: [ // 3-6
    '{n} días de cardio{name}. Cuida tu racha — muévete hoy.',
    'Llevas {n} días{name}. Una sesión hoy y vas por más.',
  ],
  4: [ // 7-13
    'Una semana de racha{name}. Hoy aún no — haz cardio y mantenla.',
    '{n} días{name}. Mantén tu racha, muévete hoy.',
  ],
  5: [ // 14-29
    '{n} días sin parar{name}. Cuida la racha — una sesión hoy.',
  ],
  6: [ // 30-59
    '🏆 {n} días{name}. No pierdas esto — cardio hoy y sigue.',
  ],
  7: [ // 60+
    '👑 {n} días{name}. Una sesión hoy y la racha élite continúa.',
  ],
};

function tierFor(streak: number): { tier: 0 | 1 | 2 | 3 | 4 | 5; bucket: number } {
  if (streak <= 0) return { tier: 0, bucket: 0 };
  if (streak === 1) return { tier: 1, bucket: 1 };
  if (streak === 2) return { tier: 1, bucket: 2 };
  if (streak <= 6)  return { tier: 2, bucket: 3 };
  if (streak <= 13) return { tier: 3, bucket: 4 };
  if (streak <= 29) return { tier: 4, bucket: 5 };
  if (streak <= 59) return { tier: 5, bucket: 6 };
  return { tier: 5, bucket: 7 };
}

function pickVariant<T>(arr: T[], seed: string): T {
  if (arr.length === 0) return arr[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash << 5) - hash + seed.charCodeAt(i);
  return arr[Math.abs(hash) % arr.length];
}

function fillTemplate(template: string, name: string | undefined, streak: number): string {
  const namePart = name ? `, ${name}` : '';
  return template.replace(/\{name\}/g, namePart).replace(/\{n\}/g, String(streak));
}

export function getStreakDisplay(
  streak: number,
  options: StreakOptions | 'nutrition' | 'cardio' = 'nutrition',
): StreakDisplay {
  // Backwards compat: firma anterior aceptaba string como segundo arg
  const opts: StreakOptions = typeof options === 'string'
    ? { activity: options }
    : options;

  const activity = opts.activity || 'nutrition';
  const firstName = opts.clientFirstName?.split(/\s+/)[0];
  const loggedToday = opts.loggedToday ?? true; // default a "ya registró hoy" para retrocompat

  const seed = opts.seed || `${firstName || 'anon'}-${activity}-${streak}-${loggedToday}`;
  const { tier, bucket } = tierFor(streak);

  let pool: string[];
  if (activity === 'cardio') {
    pool = (loggedToday ? CARDIO_LOGGED_TODAY : CARDIO_NOT_LOGGED_TODAY)[bucket] || [];
  } else {
    pool = (loggedToday ? NUTRITION_LOGGED_TODAY : NUTRITION_NOT_LOGGED_TODAY)[bucket] || [];
  }

  const template = pickVariant(pool, seed);
  const message = fillTemplate(template, firstName, streak);

  return { emoji: '', message, tier };
}

export function streakCardClasses(tier: number): string {
  switch (tier) {
    case 0:
      return 'border-secondary bg-card';
    case 1:
      return 'border-secondary bg-card';
    case 2:
      return 'border-orange-400/40 bg-orange-50/30 dark:bg-orange-950/20';
    case 3:
      return 'border-orange-500/50 bg-orange-50/40 dark:bg-orange-950/30';
    case 4:
      return 'border-amber-500/60 bg-amber-50/40 dark:bg-amber-950/30';
    case 5:
      return 'border-yellow-500/70 bg-gradient-to-br from-yellow-50/60 to-amber-50/60 dark:from-yellow-950/30 dark:to-amber-950/30 shadow-lg shadow-yellow-500/10';
    default:
      return 'border-secondary bg-card';
  }
}

export function streakIconClass(tier: number): string {
  switch (tier) {
    case 0:
    case 1:
      return 'text-muted-foreground';
    case 2:
      return 'text-orange-500';
    case 3:
      return 'text-orange-600';
    case 4:
      return 'text-amber-500';
    case 5:
      return 'text-yellow-500';
    default:
      return 'text-muted-foreground';
  }
}
